import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bookingService } from '../services/bookingService';
import {
  CancelBookingRequest,
  AdjustCheckInTimeRequest,
  AdjustCheckOutTimeRequest,
} from '../types/booking';

export const BOOKINGS_QUERY_KEY = 'bookings';

/**
 * Pure query-options factory — exported so Bookings.tsx can pass it to useQueries
 * without importing bookingService directly (constitution §III-D).
 */
export const bookingQueryOptions = (apartmentId: string) => ({
  queryKey: [BOOKINGS_QUERY_KEY, apartmentId] as const,
  queryFn: () => bookingService.getByApartment(apartmentId),
  enabled: !!apartmentId,
});

type BookingRefetchableQuery = {
  refetch: (options?: { throwOnError?: boolean }) => Promise<unknown>;
};

export const refetchBookingQueries = async (queries: readonly BookingRefetchableQuery[]) => {
  await Promise.all(queries.map(query => query.refetch({ throwOnError: true })));
};

/** Fetches all bookings for a single apartment. */
export const useBookingsByApartment = (apartmentId: string) =>
  useQuery(bookingQueryOptions(apartmentId));

/** Mutation: cancel a booking. Invalidates the full bookings cache on success. */
export const useCancelBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CancelBookingRequest }) =>
      bookingService.cancelBooking(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BOOKINGS_QUERY_KEY] });
    },
  });
};

// ── 022-checkin-checkout-times ────────────────────────────────────────────────

/** Mutation: record manual check-in. Invalidates all bookings on success. */
export const useCheckIn = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (bookingId: string) => bookingService.checkIn(bookingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BOOKINGS_QUERY_KEY] });
    },
  });
};

/** Mutation: record manual check-out. Invalidates all bookings on success. */
export const useCheckOut = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (bookingId: string) => bookingService.checkOut(bookingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BOOKINGS_QUERY_KEY] });
    },
  });
};

/** Mutation: adjust scheduled check-in time. Returns updated booking. */
export const useAdjustCheckInTime = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AdjustCheckInTimeRequest }) =>
      bookingService.adjustCheckInTime(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BOOKINGS_QUERY_KEY] });
    },
  });
};

/**
 * Mutation: adjust scheduled check-out time.
 * Returns AdjustCheckOutTimeResult — { type: 'applied' | 'pending', booking }.
 * Callers must inspect `result.type` to show correct feedback toast.
 */
export const useAdjustCheckOutTime = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AdjustCheckOutTimeRequest }) =>
      bookingService.adjustCheckOutTime(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BOOKINGS_QUERY_KEY] });
    },
  });
};

// ── end 022-checkin-checkout-times ─────────────────────────────────────────────

// ── 032-booking-recovery-linking ──────────────────────────────────────────────

/**
 * Fetches a single booking by ID.
 * Used by the Support Cases panel to derive eligible recovery actions without
 * requiring the caller to pass the booking object down from the list.
 */
export const useBooking = (bookingId: string | undefined) =>
  useQuery({
    queryKey: [BOOKINGS_QUERY_KEY, 'detail', bookingId] as const,
    queryFn: () => bookingService.getById(bookingId!),
    enabled: !!bookingId,
  });

export const useLinkBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingId, email }: { bookingId: string; email: string }) =>
      bookingService.linkBooking(bookingId, email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BOOKINGS_QUERY_KEY] });
    },
  });
};

// ── end 032-booking-recovery-linking ──────────────────────────────────────────
